import { useEffect, useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { getClientFundsData } from "../../api/clientFunds";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import styles from "./FundsPage.module.css";

const COLUMNS = [
  { key: "broker_name", label: "Broker Name" },
  { key: "broker_client_code", label: "Client Code" },
  { key: "client_name", label: "Client Name" },
  { key: "available_margin", label: "Available Margin" },
  { key: "total_margin", label: "Total Margin" },
  { key: "margin_used", label: "Margin Used" },
  { key: "collateral_value", label: "Collateral Value" },
  { key: "cash_margin", label: "Cash Margin" },
  { key: "notional_cash", label: "Notional Cash" },
];

const NUMERIC_KEYS = [
  "initial_margin",
  "cash_margin",
  "collateral_value",
  "notional_cash",
  "margin_used",
  "available_margin",
  "total_margin",
];

function formatNumber(value) {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-IN");
}

function FundsPage() {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setError("");
      try {
        const response = await getClientFundsData();
        const list = Array.isArray(response) ? response : response?.data ?? [];
        if (!cancelled) setData(list);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || err.message || "Failed to load funds data");
          setData([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  const totals = useMemo(() => {
    const t = { count: data.length };
    NUMERIC_KEYS.forEach((key) => {
      t[key] = data.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
    });
    return t;
  }, [data]);

  return (
    <div className={styles["container"]}>
      <div className={styles["content-wrapper"]}>
        <div className={styles["table-section"]}>
          <h2 className={styles["section-title"]}>
            <GlobalIcon iconName="FundsIcon" className={styles["section-title-icon"]} />
            FUNDS
          </h2>

          <TableContainer component={Paper} className={styles["table-container"]}>
            <Table className={styles["table"]} size="small" stickyHeader>
              <TableHead>
                <TableRow className={styles["header-row"]}>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.key} className={styles["header-cell"]}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} className={styles["empty-message-cell"]}>
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} className={styles["empty-message-cell"]}>
                      {error}
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} className={styles["empty-message-cell"]}>
                      Funds not available.
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {data.map((row, index) => (
                      <TableRow key={row.client ?? index} className={styles["data-row"]}>
                        <TableCell className={styles["broker-name-cell"]}>
                          {row.broker_name ?? "—"}
                        </TableCell>
                        <TableCell className={styles["data-cell"]}>
                          {row.broker_client_code ?? "—"}
                        </TableCell>
                        <TableCell className={styles["data-cell"]}>
                          {row.client_name ?? "—"}
                        </TableCell>
                        <TableCell className={styles["numeric-cell"]}>
                          {formatNumber(row.available_margin)}
                        </TableCell>
                        <TableCell className={styles["numeric-cell"]}>
                          {formatNumber(row.total_margin)}
                        </TableCell>
                        <TableCell className={styles["numeric-cell"]}>
                          {formatNumber(row.margin_used)}
                        </TableCell>
                        <TableCell className={styles["numeric-cell"]}>
                          {formatNumber(row.collateral_value)}
                        </TableCell>
                        <TableCell className={styles["numeric-cell"]}>
                          {formatNumber(row.cash_margin)}
                        </TableCell>
                        <TableCell className={styles["numeric-cell"]}>
                          {formatNumber(row.notional_cash)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className={styles["total-row"]}>
                      <TableCell className={styles["total-cell"]}>Total</TableCell>
                      <TableCell className={styles["total-cell"]}>{totals.count}</TableCell>
                      <TableCell className={styles["total-cell"]} />
                      <TableCell className={styles["total-cell"]}>{formatNumber(totals.available_margin)}</TableCell>
                      <TableCell className={styles["total-cell"]}>{formatNumber(totals.total_margin)}</TableCell>
                      <TableCell className={styles["total-cell"]}>{formatNumber(totals.margin_used)}</TableCell>
                      <TableCell className={styles["total-cell"]}>{formatNumber(totals.collateral_value)}</TableCell>
                      <TableCell className={styles["total-cell"]}>{formatNumber(totals.cash_margin)}</TableCell>
                      <TableCell className={styles["total-cell"]}>{formatNumber(totals.notional_cash)}</TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
}

export default FundsPage;
